/**
 * Who took the demo, and who the booking really belongs to.
 *
 * Two jobs that turn up on the same row and want the same shape of control,
 * so they live together:
 *
 *   **Demo completed by** — a demo that has happened was run by somebody,
 *   and often by two people. They are not necessarily users of this
 *   workspace: a trainer or a product specialist sits in a demo without
 *   ever needing a login, and handing out accounts so that a name exists
 *   would be a licence per name. So the demo team keeps its own short list
 *   and ticks off who was in the room.
 *
 *   **Registered by hand** — the match to the Client Master is made on
 *   email and phone, which misses the client who books with a personal
 *   address. That is one press from the row's menu rather than a control
 *   of its own, so it lives on the page; what is here is the menu it is
 *   pressed from.
 *
 * Everything writes through the endpoints the panel already uses, so a
 * change made from the row shows on the booking, on the follow-up and in
 * the export without anything being kept in two places.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api.js';
import Icon from '../lib/icons.jsx';
import { Pill, Spinner } from './Primitives.jsx';

/** Initials, for the little stacked faces. */
const initials = name => String(name || '?')
  .trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

/* -------------------------------------------------------- on the row */

/** Who ran this demo, as a compact line under the name. */
export function Attendees({ list }) {
  if (!list || !list.length) return null;
  return (
    <div className="dbk-att" title={`Demo handled by ${list.map(a => a.name).join(', ')}`}>
      <span className="dbk-attfaces">
        {list.slice(0, 3).map(a => <i key={a.id}>{initials(a.name)}</i>)}
      </span>
      <b>Demo handled by {list.map(a => a.name).join(', ')}</b>
    </div>
  );
}

/**
 * The row's own menu.
 *
 * A kebab rather than two more buttons on the line: these are things done
 * once per demo, not the thing the row is for, and the row already carries
 * a Follow up button that must stay the obvious one.
 */
export function RowMenu({ items }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const live = items.filter(Boolean);

  useEffect(() => {
    if (!open) return undefined;
    const away = e => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  if (!live.length) return null;

  return (
    <div className="dbk-menu" ref={box} onClick={e => e.stopPropagation()}>
      <button
        type="button"
        className={`dbk-kebab${open ? ' on' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title="More for this demo"
        onClick={() => setOpen(v => !v)}
      >
        <Icon name="dots" />
      </button>
      {open ? (
        <div className="dbk-menubox" role="menu">
          {live.map(it => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); it.run(); }}
            >
              <Icon name={it.icon} />
              <span>
                <b>{it.label}</b>
                {it.hint ? <small>{it.hint}</small> : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------ demo completed by */

/**
 * Tick off who was in the room, and add a name without leaving the row.
 *
 * Somebody new joining the team turns up for the first time in the middle
 * of recording a demo, so the list can be added to from here; the addition
 * is the same list the Assignees window manages.
 */
export function AttendeePicker({ s, list, onCancel, onDone, toast, reloadAssignees }) {
  const [picked, setPicked] = useState(new Set(s.attendedBy || []));
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState('');
  const offer = useMemo(
    () => list.filter(a => a.active || picked.has(a.id)),
    [list, picked]
  );

  const toggle = id => setPicked(p => {
    const next = new Set(p);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const addNew = async () => {
    const name = adding.trim();
    if (!name) return;
    setBusy('add');
    try {
      const r = await api.calendly.addAssignee(name);
      setAdding('');
      setPicked(p => new Set([...p, r.assignee.id]));
      if (reloadAssignees) reloadAssignees(r.assignees);
      toast(`${name} added to the demo team`);
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    setBusy('save');
    try {
      await api.calendly.update(s.id, { attendedBy: [...picked] });
      toast(picked.size ? 'Recorded who took the demo' : 'Cleared who took the demo');
      onDone();
    } catch (e) {
      toast(e.message);
      setBusy('');
    }
  };

  return (
    <div className="dbk-start picking" onClick={e => e.stopPropagation()}>
      <label>
        <span>Who took this demo?</span>
      </label>

      {offer.length ? (
        <div className="dbk-chips">
          {offer.map(a => (
            <button
              key={a.id}
              type="button"
              className={`dbk-chip${picked.has(a.id) ? ' on' : ''}${a.active ? '' : ' gone'}`}
              onClick={() => toggle(a.id)}
            >
              <i>{initials(a.name)}</i>
              {a.name}
              {picked.has(a.id) ? <Icon name="check" /> : null}
            </button>
          ))}
        </div>
      ) : (
        <p className="dbk-hint">Nobody on the demo team list yet — add the first name below.</p>
      )}

      <div className="dbk-addfb">
        <input
          value={adding}
          placeholder="Add somebody new to the demo team…"
          onChange={e => setAdding(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNew(); } }}
        />
        <button type="button" className="btn sm" disabled={!adding.trim() || !!busy} onClick={addNew}>
          {busy === 'add' ? <Spinner /> : <Icon name="plus" />}Add
        </button>
      </div>

      <div className="dbk-startacts">
        <button type="button" className="btn sm primary" disabled={!!busy} onClick={save}>
          {busy === 'save' ? <Spinner /> : <Icon name="check" />}Save
        </button>
        <button type="button" className="btn sm" disabled={!!busy} onClick={onCancel}>Cancel</button>
        <span className="dim">It shows on the booking, on the follow-up and in the export.</span>
      </div>
    </div>
  );
}

/* ---------------------------------------------- registered, by hand */

const PLANS = ['Free', 'Starter', 'Growth', 'Suite'];

/**
 * Which plan they are on.
 *
 * A matched client reads its plan off the clients app; a booking marked
 * registered by hand has no account to read it from, so the person who
 * knows says — and the row carries it, because "registered" without a plan
 * answers half the question anybody was asking.
 */
export function PlanPicker({ s, onCancel, onDone, toast }) {
  const [plan, setPlan] = useState(s.manualPlan || 'Free');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.calendly.update(s.id, { manualRegistered: true, manualPlan: plan });
      toast(`Marked as a registered client — ${plan}`);
      onDone();
    } catch (e) {
      toast(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="dbk-start linking" onClick={e => e.stopPropagation()}>
      <label><span>They are a client — which plan?</span></label>
      <div className="dbk-chips">
        {PLANS.map(p => (
          <button
            key={p}
            type="button"
            className={`dbk-chip${plan === p ? ' on' : ''}`}
            onClick={() => setPlan(p)}
          >
            {p}
            {plan === p ? <Icon name="check" /> : null}
          </button>
        ))}
      </div>
      <div className="dbk-startacts">
        <button type="button" className="btn sm primary" disabled={busy} onClick={save}>
          {busy ? <Spinner /> : <Icon name="check" />}Mark as registered
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={onCancel}>Cancel</button>
        <span className="dim">Recorded by hand, so a resync will not undo it.</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------- the list itself */

/**
 * The demo team list: add, rename, remove.
 *
 * Removing somebody who has run demos does not erase them from those demos
 * — the record of who was in the room should not rewrite itself when a
 * person leaves — so they are retired instead and stop being offered.
 */
export function AssigneeBook({ list, onClose, onChange, toast, mayEdit }) {
  const [name, setName] = useState('');
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState('');

  const run = async (what, fn, done) => {
    setBusy(what);
    try {
      const r = await fn();
      if (r && r.assignees) onChange(r.assignees);
      if (done) done(r);
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="dbk-modal" role="dialog" aria-modal="true" aria-label="The demo team">
      <div className="dbk-modalbg" onClick={onClose} />
      <div className="dbk-modalbox">
        <header>
          <div className="ic g-purple"><Icon name="users" solid /></div>
          <div>
            <h4>The demo team</h4>
            <p>Who can be recorded as having taken a demo.</p>
          </div>
          <button type="button" className="dbk-x" onClick={onClose} title="Close"><Icon name="x" /></button>
        </header>

        {mayEdit ? (
          <div className="dbk-addfb dbk-addrow">
            <input
              value={name}
              placeholder="Name…"
              onChange={e => setName(e.target.value)}
              onKeyDown={e => {
                if (e.key !== 'Enter' || !name.trim()) return;
                e.preventDefault();
                run('add', () => api.calendly.addAssignee(name.trim()), () => { toast(`${name.trim()} added`); setName(''); });
              }}
            />
            <button
              type="button"
              className="btn sm primary"
              disabled={!name.trim() || !!busy}
              onClick={() => run('add', () => api.calendly.addAssignee(name.trim()), () => { toast(`${name.trim()} added`); setName(''); })}
            >
              {busy === 'add' ? <Spinner /> : <Icon name="plus" />}Add
            </button>
          </div>
        ) : null}

        {list.length ? (
          <table className="dbk-people">
            <thead>
              <tr><th>Name</th><th>Status</th><th aria-label="Actions" /></tr>
            </thead>
            <tbody>
              {list.map(a => (
                <tr key={a.id} className={a.active ? '' : 'gone'}>
                  <td>
                    {editing === a.id ? (
                      <input
                        autoFocus
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Escape') setEditing(null);
                          if (e.key !== 'Enter' || !draft.trim()) return;
                          e.preventDefault();
                          run('save', () => api.calendly.renameAssignee(a.id, draft.trim()), () => { toast('Renamed'); setEditing(null); });
                        }}
                      />
                    ) : (
                      <div className="dbk-person">
                        <i>{initials(a.name)}</i>
                        <b>{a.name}</b>
                      </div>
                    )}
                  </td>
                  <td>
                    {a.active
                      ? <Pill tone="gr-teal" icon="check" className="dbk-client">On the team</Pill>
                      : <Pill tone="gr-slate" className="dbk-client">Retired</Pill>}
                  </td>
                  <td className="dbk-rowacts">
                    {!mayEdit ? null : editing === a.id ? (
                      <>
                        <button type="button" className="btn sm primary" disabled={!draft.trim() || !!busy}
                          onClick={() => run('save', () => api.calendly.renameAssignee(a.id, draft.trim()), () => { toast('Renamed'); setEditing(null); })}>
                          Save
                        </button>
                        <button type="button" className="btn sm" onClick={() => setEditing(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="dbk-icon" title="Rename"
                          onClick={() => { setEditing(a.id); setDraft(a.name); }}>
                          <Icon name="edit" />
                        </button>
                        <button type="button" className="dbk-icon danger" title={a.active ? 'Remove' : 'Put back on the team'}
                          disabled={!!busy}
                          onClick={() => {
                            if (!a.active) {
                              run('save', () => api.calendly.setAssigneeActive(a.id, true), () => toast(`${a.name} is back on the team`));
                              return;
                            }
                            run('del', () => api.calendly.removeAssignee(a.id),
                              r => toast(r.retired
                                ? `${a.name} has run demos, so they are retired rather than deleted`
                                : `${a.name} removed`));
                          }}>
                          <Icon name={a.active ? 'trash' : 'undo'} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="dbk-hint dbk-nobody">Nobody yet. Add the people who run the demos and they can be ticked off on each one.</p>
        )}

        <footer>
          <span className="dim">Renaming somebody renames them on every demo they ran.</span>
          <button type="button" className="btn" onClick={onClose}>Done</button>
        </footer>
      </div>
    </div>
  );
}
